import { lazy, Suspense, useMemo, useState } from 'react';
import { useLanguage } from '@/components/utils/useLanguage';
import {
  useActivities,
  useCategories,
  useDeals,
  useFeatures,
  useLabels,
  useSeasons,
  useTodayWeather,
  useWeatherByDate,
  useWeekWeather,
} from '@/hooks/useSharedData';
import { getTimeSlot, getTimeSlotLabel } from '../components/utils/activityScoring';
import { buildDayOptions, defaultDayOffset, dayTitle, weekdayKey } from '@/components/home/dayPicker';
import { buildDayWeatherLine, conditionLabel } from '@/components/home/dayWeather';
import { selectDayActivities } from '@/components/home/dayMatch';
import { filtersToParams } from '../utils/filterUrlParams';
import { useFavorites } from '../components/utils/useFavorites';
import HeroSection from '@/components/home/sections/HeroSection';
import NowMatchSection from '@/components/home/sections/NowMatchSection';
import SectionSkeleton from '@/components/home/SectionSkeleton';
import { sectionBgAmber, sectionBgLight } from '@/lib/ds';

// Below the fold: every section is its own chunk, so the initial bundle only
// carries the hero and the first carousel (LCP-critical).
const FavoritesSection = lazy(() => import('@/components/home/sections/FavoritesSection'));
const PopularSection = lazy(() => import('@/components/home/sections/PopularSection'));
const CategoriesSection = lazy(() => import('@/components/home/sections/CategoriesSection'));
const AgeGroupsSection = lazy(() => import('@/components/home/sections/AgeGroupsSection'));
const DealsSection = lazy(() => import('@/components/home/sections/DealsSection'));
const AboutFaqSection = lazy(() => import('@/components/home/sections/AboutFaqSection'));
const UpdatesSection = lazy(() => import('@/components/home/sections/UpdatesSection'));

export default function Home() {
  const { language } = useLanguage();
  const { favorites } = useFavorites();
  // Shared cache: the same lists serve Ausflugsideen and the header search, so
  // they are fetched once per visit instead of once per page.
  const { data: allActivities = [], isLoading: loading } = useActivities();
  const { data: categories = [] } = useCategories();
  const { data: deals = [] } = useDeals();
  const { data: labels = [] } = useLabels();
  const { data: seasons = [] } = useSeasons();
  const { data: features = [] } = useFeatures();
  const { data: todayWeather = null } = useTodayWeather();
  const { data: weatherByDate = {} } = useWeatherByDate();
  const { data: weekWeather = null } = useWeekWeather();

  const currentSlot = getTimeSlot();
  const timeSlotLabel = getTimeSlotLabel(currentSlot, language);
  const slotWeather = useMemo(() => todayWeather
    ? {
        condition: todayWeather.slots?.[currentSlot]?.condition || todayWeather.condition,
        temperature: todayWeather.slots?.[currentSlot]?.temperature ?? todayWeather.temperature_max,
      }
    : null, [todayWeather, currentSlot]);

  // Day picker: standard is today, from 19:00 tomorrow. A pick is never stored,
  // so every visit starts from the standard again.
  const now = useMemo(() => new Date(), []);
  const dayOptions = useMemo(() => buildDayOptions(now, language), [now, language]);
  const [selectedDayKey, setSelectedDayKey] = useState(() => dayOptions[defaultDayOffset(now)]?.key);
  const selectedDay = dayOptions.find(d => d.key === selectedDayKey) || dayOptions[0];

  // Today keeps the live slot line, every other day reads its stored forecast.
  const dayForecast = selectedDay.isToday ? slotWeather : (weatherByDate[selectedDay.key] || null);
  const weather = selectedDay.isToday
    ? {
        condition: slotWeather?.condition || null,
        text: slotWeather
          ? [
              `${timeSlotLabel}${slotWeather.temperature != null ? ` ${slotWeather.temperature}°` : ''}`,
              conditionLabel(slotWeather.condition, language),
            ].filter(Boolean).join(', ')
          : null,
      }
    : buildDayWeatherLine(dayForecast, selectedDay, language, now);

  // Weather fit of the picked day orders the cards; today's fit comes from the
  // current slot, other days from the forecast of that date.
  const fitWeather = useMemo(() => selectedDay.isToday
    ? slotWeather
    : (dayForecast ? { condition: dayForecast.condition, temperature: dayForecast.temperature_max } : null),
    [selectedDay.isToday, slotWeather, dayForecast]);

  const dayActivities = useMemo(() => selectDayActivities(allActivities, {
    weekdayIndex: selectedDay.weekdayIndex,
    weather: fitWeather,
    seasons,
  }), [allActivities, selectedDay.weekdayIndex, fitWeather, seasons]);

  const dayFilterLink = `/ausflugsideen?${filtersToParams({ tage: [weekdayKey(selectedDay.weekdayIndex)] }).toString()}`;

  const popularActivities = allActivities
    .filter(a => a.popularity_score > 0)
    .sort((a, b) => (b.popularity_score || 0) - (a.popularity_score || 0))
    .slice(0, 10);

  const recentActivities = allActivities
    .sort((a, b) => new Date(b.created_date) - new Date(a.created_date))
    .slice(0, 6);

  const favoriteActivities = allActivities
    .filter(a => favorites.includes(a.id))
    .filter(a => weekWeather ? (a.weather_suitable || []).includes(weekWeather.condition) : true)
    .slice(0, 10);

  const activeDeals = deals.filter(d => !d.valid_until || new Date(d.valid_until) >= new Date()).slice(0, 3);
  const dealActivities = allActivities.filter(a => activeDeals.some(d => d.activity_id === a.id));

  const newestFeatures = [...features]
    .sort((a, b) => {
      const dateIdx = (x) => x?.new_year && x?.new_month ? x.new_year * 12 + x.new_month : -1;
      const da = dateIdx(a), db = dateIdx(b);
      if (da !== db) return db - da;
      return (a.sort_order || 0) - (b.sort_order || 0);
    })
    .slice(0, 5);

  return (
    <div className="min-h-screen">
      {/* Above the fold — rendered with the initial chunk */}
      <HeroSection />

      <NowMatchSection
        title={dayTitle(selectedDay, language)}
        activities={dayActivities}
        loading={loading}
        categories={categories}
        labels={labels}
        deals={deals}
        weather={weather}
        ctaTo={dayFilterLink}
        dayOptions={dayOptions}
        selectedDay={selectedDay}
        selectedDayKey={selectedDayKey}
        onSelectDay={setSelectedDayKey}
      />

      {favoriteActivities.length > 0 && (
        <Suspense fallback={<SectionSkeleton background={sectionBgAmber} />}>
          <FavoritesSection
            activities={favoriteActivities}
            categories={categories}
            labels={labels}
            deals={deals}
            weekCondition={weekWeather?.condition || null}
          />
        </Suspense>
      )}

      <Suspense fallback={<SectionSkeleton />}>
        <PopularSection
          activities={popularActivities}
          fallbackActivities={recentActivities}
          loading={loading}
          categories={categories}
          labels={labels}
          deals={deals}
        />
      </Suspense>

      <Suspense fallback={<SectionSkeleton variant="sliders" background={sectionBgLight} />}>
        <CategoriesSection categories={categories} activities={allActivities} loading={loading} />
      </Suspense>

      <Suspense fallback={<SectionSkeleton variant="sliders" />}>
        <AgeGroupsSection activities={allActivities} />
      </Suspense>

      {activeDeals.length > 0 && (
        <Suspense fallback={<SectionSkeleton />}>
          <DealsSection deals={activeDeals} activities={dealActivities} />
        </Suspense>
      )}

      <Suspense fallback={<SectionSkeleton variant="text" />}>
        <AboutFaqSection />
      </Suspense>

      {newestFeatures.length > 0 && (
        <Suspense fallback={<SectionSkeleton background={sectionBgLight} />}>
          <UpdatesSection features={newestFeatures} />
        </Suspense>
      )}
    </div>
  );
}